import { Button } from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { isDefined } from "@bfc-software/app-toolbox";
import { type PageToken } from "@bfc-software/bfc-web-ui/lib/cursor-pagination";

import styles from "./audit-pager.module.css";

/** The `AuditEventPage` envelope, minus `items`. */
type PaginationState = {
  has_next: boolean;
  has_prev: boolean;
  next_cursor?: string;
  prev_cursor?: string;
};

/**
 * Forward/back controls for the audit log's cursor pagination.
 *
 * Unlike `CursorPager` (ui/shared), Previous really works here: the audit
 * service's keyset pagination reports `has_prev`/`prev_cursor` on every
 * response (handler_audit_events.go), not only on a subset of listings, so
 * there is no "not yet answerable" case to render disabled. Kept local to
 * this route rather than folded into `CursorPager` -- that component's
 * `Previous` is permanently disabled by a documented, task-planner-specific
 * limitation, and changing its shape to fit a different service is a decision
 * for whoever owns that migration, not a side effect of this feature.
 */
export function AuditPager({
  pagination,
  isFetching,
  onChange,
  t,
}: {
  pagination?: PaginationState;
  isFetching: boolean;
  onChange: (page: PageToken) => void;
  t: TFunction;
}) {
  const nextCursor =
    pagination?.has_next === true ? pagination.next_cursor : undefined;
  const prevCursor =
    pagination?.has_prev === true ? pagination.prev_cursor : undefined;

  return (
    <nav
      className={styles.pager}
      aria-label={t("auditLog.pagination.ariaLabel")}
      data-test-role="audit-pager"
    >
      <div className={styles.controls}>
        <Button
          kind="text-icon-left"
          variant="tertiary"
          icon="ChevronLeft"
          text={t("pagination.previous")}
          isDisabled={!isDefined(prevCursor) || isFetching}
          onPress={() =>
            isDefined(prevCursor) &&
            onChange({ cursor: prevCursor, dir: "prev" })
          }
          data-test-role="audit-pager-previous"
        />
        <Button
          kind="text-icon-right"
          variant="tertiary"
          icon="ChevronRight"
          text={t("pagination.next")}
          isDisabled={!isDefined(nextCursor) || isFetching}
          onPress={() =>
            isDefined(nextCursor) &&
            onChange({ cursor: nextCursor, dir: "next" })
          }
          data-test-role="audit-pager-next"
        />
      </div>
    </nav>
  );
}
